import { Box, BoxProps, convertHexToRGBA, Text } from '@jfs/ui-new'
import { isIE } from '@jfs/utils'
import { FC } from 'react'
import Footer from '../../comps/Footer'
import DottedImage, { dottedImageTypes } from '../../comps/ImageDotted'
import { LinearGradientContainer } from '../../comps/LinearGradient'
import { SvgImage } from '../../comps/SvgImages'
import useFontSize from '../../util/useFontSize'
import usePadding from '../../util/usePadding'
import { isMobileWidth, isTabletWidth, useWindowDimensions } from '../../util/util'
import ContactForm from './ContactForm'

interface ContactUsProps {
  type?: 'screen' | 'footer'
  footerNote?: string
}

const ContactUs: FC<ContactUsProps> = ({ type = 'screen', footerNote }: ContactUsProps) => {
  const viewPortWidth = useWindowDimensions()
  const resPadding = usePadding()
  const themedStyle = styles(viewPortWidth, resPadding)

  const isTab = viewPortWidth < 1024
  const borderRadius = type === 'footer' ? (isTab ? 32 : 120) : 0
  return (
    <LinearGradientContainer
      colorArray={[convertHexToRGBA('#D1E2FF', 40), convertHexToRGBA('#F1E5FF', 40)]}
      useAngle={true}
      angle={131}
      customStyle={{ borderTopRightRadius: borderRadius, borderTopLeftRadius: borderRadius }}>
      <Box {...themedStyle.outerontainer} borderTopRightRadius={borderRadius} borderTopLeftRadius={borderRadius}>
        <FormBox type={type} footerNote={footerNote} />
        <Box {...themedStyle.footerBackground}>
          <Footer />
        </Box>
      </Box>
    </LinearGradientContainer>
  )
}

interface RenderCircle {
  width: number
  type?: string
}

const RenderCircle = ({ width, type }: RenderCircle) => {
  const isMobile = isMobileWidth(width)
  const isTab = width >= 600 && width <= 1024
  const isLargeDesktop = width > 1550
  const resPadding = usePadding()

  return (
    <Box
      width={isMobile ? '100%' : '50%'}
      minWidth={isTab ? undefined : 300}
      maxWidth={isTab ? 320 : undefined}
      minHeight={isMobile ? undefined : '40vh'}
      justifyContent="flex-start"
      mt={40}>
      <Box
        top={isMobile ? -80 : isTab ? -140 : 0}
        left={isMobile ? '-10%' : isTab ? '0%' : '-28%'}
        width={isMobile ? width : isLargeDesktop ? width / 2.5 : width / 2}
        maxWidth={isTab ? 320 : undefined}
        minHeight={isMobile ? 240 : 300}
        zIndex={99}
        overflow="visible"
        height={isMobile || isTab ? undefined : '70%'}>
        <SvgImage imageName="CircleArrow" width={isMobile ? 312 : 720} height={isMobile ? 265 : 560} />
      </Box>
      <Box
        zIndex={999}
        position={'absolute'}
        top={isMobile || isTab ? 20 : 218}
        alignSelf="flex-start"
        justify={isMobile ? 'center' : 'flex-start'}
        mt={!isTab && type === 'screen' ? undefined : 0}
        ph={isMobile ? 16 : 0}>
        <Box maxWidth={isMobile ? '100%' : isIE() ? 480 : undefined}>
          <Text
            lineHeight={useFontSize().circleHeadingLineHeight}
            fontSize={useFontSize().circleHeading}
            fontFamily={'jiotype-black'}>
            {'Join JioPay\nPartner Program'}
          </Text>
        </Box>
      </Box>
    </Box>
  )
}

interface FormProps {
  type?: string
  footerNote?: string
}
const FormBox: FC<FormProps> = ({ type, footerNote }: FormProps) => {
  const viewPortWidth = useWindowDimensions()

  const isMobile = viewPortWidth < 600
  const isTablet = isTabletWidth(viewPortWidth)
  const isMobileOrTablet = isMobile || isTablet
  const resPadding = usePadding()
  const themedStyle = styles(viewPortWidth, resPadding)

  return (
    <Box>
      <Box
        {...themedStyle.formBox}
        flexDirection={isMobile ? 'column' : 'row'}
        pb={isMobile ? 0 : footerNote ? 60 : 170}>
        <RenderCircle width={viewPortWidth} type={type} />
        <Box
          width={isMobile ? '100%' : '50%'}
          alignItems="flex-end"
          minWidth={isMobile ? 220 : 300}
          pt={isMobile ? 0 : 20}
          pb={isMobile ? (footerNote ? 108 : 180) : 100}>
          <Box>
            <Box {...themedStyle.borderBox} top={isMobile ? 10 : 26} borderRadius={isMobile ? 16 : 24} />
            <Box zIndex={-99} left={isMobileOrTablet ? 30 : 140} width={'100%'} top={isMobileOrTablet ? -72 : -90}>
              <DottedImage
                opacity={0.4}
                position={dottedImageTypes.topRight}
                isDark={false}
                size={isMobileOrTablet ? 150 : undefined}
              />
            </Box>
            <Box>
              <ContactForm />
            </Box>
            <Box
              zIndex={-99}
              left={isMobileOrTablet ? -30 : -170}
              width={'100%'}
              top={100}
              nativeID={'becomePartnerFormBottomDottedImage'}>
              <DottedImage
                position={dottedImageTypes.bottomLeft}
                isDark={false}
                opacity={0.4}
                size={isMobileOrTablet ? 150 : undefined}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const styles = (breakpoint: number, responsivePadding: number) => {
  // const responsivePadding = getPadding(breakpoint)
  const isIpad = isTabletWidth(breakpoint)
  const isMobile = isMobileWidth(breakpoint)
  return {
    outerontainer: {
      display: 'flex',
      flexDirection: 'column',
      overflow: 'scroll',
    } as BoxProps,
    leftBox: {
      display: 'flex',
      flex: 1,
      width: '50%',
      minWidth: 300,
    } as BoxProps,
    buttonContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      flex: 1,
      justifyContent: breakpoint > 600 ? 'flex-start' : 'space-between',
      minHeight: 120,
    } as BoxProps,
    footerBackground: {
      marginTop: isMobile ? -80 : -140,
      bg: 'WHITE',
      borderTopLeftRadius: breakpoint > 1024 ? 120 : 32,
      borderTopRightRadius: breakpoint > 1024 ? 120 : 32,
    } as BoxProps,
    formBox: {
      display: 'flex',
      flexDirection: 'column',
      flexWrap: isIpad || isMobile ? 'wrap' : 'nowrap',
      justifyContent: 'space-between',
      pl: responsivePadding as Number,
      pr: responsivePadding,
      pt: 80,
    } as BoxProps,
    borderBox: {
      borderRightWidth: 2,
      borderBottomWidth: 2,
      height: isMobile ? '100%' : '98%',
      width: '100%',
      position: 'absolute',
      zIndex: -1,
      top: 26,
      left: 10,
      borderRightColor: 'BLUE',
      borderBottomColor: 'BLUE',
      borderRadius: 24,
    } as unknown as BoxProps,
  }
}

export default ContactUs
